import $ from 'cash-dom';
import FormValidator from './FormValidator';
import httpRequest from '../../components/http-request';
import spinner from '../../components/spinner';
import animate from "../../components/animate";
import ScoreTooltip from "../../components/score-tooltip";
import ProgressBar from "../../components/ProgressBar";

/**
 * Class FormHandler - works with form, validates, sends data to backend.
 * We use class to have possibility do this:
 * ```
 // example of creating FormHandler(formSelector, validationOptions)
 const contactUsFormProcessor = new FormHandler('.contact-us-form form', {
    'first-name': ['required', 'alpha', 'max|255'],
    'last-name': ['required', 'alpha', 'max|255'],
    'email': ['required', 'email'],
    'phone': ['phone'],
    'message': ['required', 'max|1000'],
  'g-recaptcha-response': ['required'],
  });

 contactUsFormProcessor.mount();

 // instead of setTimeout could be a lot of some code
 setTimeout(() => {
    contactUsFormProcessor.unmount(); // unmount() offs listening submit event.
 }, 2000)
 * ```
 */
class FormHandler {
  /**
   * FormHandler constructor
   * @param {string} formSelector jQuery selector of form.
   * @param {Object} validationOptions Object in format {'input-field-name': ['validationRuleName']}.
   * See ./FormValidator.js for details.
   * @param {string} unknownErrorSelector
   */
  constructor(formSelector, validationOptions, unknownErrorSelector= null) {
    this._$form = $(formSelector);
    this._validationOptions = validationOptions;
    this._$unknownErrorHolder = unknownErrorSelector ? $(unknownErrorSelector) : null;
    this._onValidCallback = null;

    this._formValidator = new FormValidator(this._$form);
	this._progressBar = new ProgressBar('.progress-bar-holder');
  }

  onValid(callbackFn) {
    if (typeof callbackFn !== 'function') {
      return;
    }

    this._onValidCallback = callbackFn;
  }

  onSuccessResponse(callbackFn) {
    if (typeof callbackFn !== 'function') {
      return;
    }

    this._onSuccessResponseCallback = callbackFn;
  }

  onErrorResponse(callbackFn) {
    if (typeof callbackFn !== 'function') {
      return;
    }

    this._onErrorResponseCallback = callbackFn;
  }

  /**
   * Starts listen on submit event.
   */
  mount() {
    this._$form.on('submit', this._submitCallback);
  }

  _submitCallback = (e) => {
    this._handleSubmit(e);
  };

  _handleSubmit(e) {
    e.preventDefault();
    const form = e.target;

    this._setUnknownError(null);
    this._formValidator.clearErrors();

    if (this._validationOptions === null) {
      if (typeof this._onValidCallback === 'function') {
        this._onValidCallback(e);
      } else {
        const formData = new FormData(form);
        this._sendToBackend(formData);
      }
    } else {
      this._formValidator.validateAsync(this._validationOptions).then((hasErrors) => {
        if (hasErrors) {
          return;
        }

        if (typeof this._onValidCallback === 'function') {
          this._onValidCallback(e);
        } else {
          const formData = new FormData(form);
          this._sendToBackend(formData);
        }
      });
    }
  }

  _sendToBackend(formData) {
    spinner.show();
    const self = this;

	if (self._progressBar) {
		self._progressBar._start();
	}

    httpRequest.post(ajaxurl, formData, function (error, response) {
      if (error) {
        self._handleRequestError(error);
      } else {
        self._handleResponse(response);
      }

      Alooma.applyAloomaTracking();
      ScoreTooltip.init();
      spinner.hide();

	  if (self._progressBar) {
	  	self._progressBar._finish();
	  }
    }, 'json');
  }

  _handleResponse = (data) => {
    if (data.success) {
      if (typeof this._onSuccessResponseCallback === 'function') {
        this._onSuccessResponseCallback(data);
      } else {
        // go to url with query param /?status=success
        const ulr = new URL(window.location.href);
        ulr.searchParams.set('status', 'success');
        window.location.href = ulr.href;
      }
    } else if (data.errors) {
      if (typeof this._onErrorResponseCallback === 'function') {
        this._onErrorResponseCallback(data.errors);
      } else {
        this._formValidator.setValidationErrors(data.errors);
      }
    } else {
      this._setUnknownError(tr('Sorry, some unknown error occurred.'));
    }
  };

  _handleRequestError = (error) => {
    this._setUnknownError(tr('Sorry, some unknown error occurred.'));
  };

  _setUnknownError(errorMessage) {
    if (!this._$unknownErrorHolder || !this._$unknownErrorHolder.length) {
      return;
    }

    this._$unknownErrorHolder.text(errorMessage);

    if (errorMessage === null) {
      this._$unknownErrorHolder.addClass('hidden');
    } else {
      this._$unknownErrorHolder.removeClass('hidden');
      animate.scrollToTop(0);
    }
  }
}

export default FormHandler;
